"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, Sparkles } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList: {
  id: string;
  question: string;
  answer: React.ReactNode;
}[] = [{
  id: "item-1",
  question: "What is national SEO and how does it differ from local SEO?",
  answer: "National SEO focuses on capturing search visibility across the entire UK rather than targeting specific geographic locations or Google Map packs. While local SEO attracts customers in particular towns or cities, national SEO targets broader, non-location-specific search terms to reach prospective clients nationwide regardless of where they are based."
}, {
  id: "item-2",
  question: "How long does national SEO typically take to show results?",
  answer: "National SEO is a cumulative strategic process. Initial technical improvements, indexation fixes, and movement on long-tail keywords usually begin showing within the first few months. Achieving competitive movement for high-volume nationwide search terms takes longer, as building topical authority and nationwide trust develops progressively over time."
}, {
  id: "item-3",
  question: "Can national SEO be combined with local SEO or PPC?",
  answer: "Yes, combining channels is often highly effective. If your business operates nationally but also maintains physical regional offices, combining nationwide content with targeted local landing pages ensures full market coverage. Integrating PPC alongside national SEO can also drive immediate visibility while organic rankings mature."
}, {
  id: "item-4",
  question: "Are first-page Google rankings guaranteed?",
  answer: "No, no ethical consultant can guarantee specific positions on Google. Search engine algorithms evolve constantly and competitors actively adjust their strategies. What I deliver instead is transparent, evidence-based execution, rigorous technical optimization, and a clear focus on driving qualified organic leads."
}, {
  id: "item-5",
  question: "How does working with an independent consultant differ from an agency?",
  answer: "Unlike traditional agencies that pass your account to junior executives or account managers, working with me means direct access to the specialist executing your strategy—delivering direct accountability and zero agency bureaucracy from day one."
}, {
  id: "item-6",
  question: "What factors determine the cost of national SEO in the UK?",
  answer: "Pricing depends on the scope of work rather than a rigid static package. Primary cost drivers include your website's current technical foundation, the competitive landscape of your UK industry sector, the volume of content required, and the degree of authority building needed to outperform established market leaders."
}, {
  id: "item-7",
  question: "Is national SEO suitable for small or mid-sized businesses?",
  answer: "Yes, absolutely. If your company ships products or delivers services nationwide, national SEO allows you to compete with larger brands. By targeting high-intent niche queries and building strong topical depth, smaller businesses can effectively capture market share in specific product or service categories."
}, {
  id: "item-8",
  question: "Do you offer international SEO if my business expands beyond the UK?",
  answer: <span>
          Yes. If your growth plan extends beyond the UK, I provide dedicated multi-region and multi-language strategy. You can read more about how I support global campaigns on my{" "}
          <Link to="/InternationalSeoPage" className="text-primary underline hover:text-primary/80 font-medium">
            International SEO
          </Link>{" "}
          page.
        </span>
}];
export default function Nationalfaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="nationalseofaq" data-nav="light" className="relative bg-background py-16 sm:py-24 text-foreground overflow-hidden">
      <div className="aria-hidden:true pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 size-[600px] rounded-full bg-secondary/30 blur-3xl opacity-50" />
      <div className="aria-hidden:true pointer-events-none absolute bottom-0 right-0 size-[400px] rounded-full bg-primary/5 blur-3xl opacity-40" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs uppercase tracking-wider font-semibold">
            <Sparkles className="size-3.5 text-accent" />
            <span>National SEO Clarity</span>
          </Badge>

          <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Frequently Asked Questions
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Honest, straightforward answers about how I plan, execute, and deliver sustainable national search growth directly for your business.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqList.map((faq, i) => <motion.div key={faq.id} variants={itemVariants} data-index={i}>
                <AccordionItem value={faq.id} className="rounded-xl border border-border bg-card px-5 sm:px-6 shadow-sm transition-all hover:border-primary/30 data-[state=open]:border-primary/50 data-[state=open]:shadow-md">
                  <AccordionTrigger className="font-default text-base sm:text-lg font-semibold text-card-foreground hover:no-underline py-5 text-left gap-4">
                    <span className="flex items-start gap-3">
                      <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pb-5 pl-8 sm:pl-8">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mx-auto max-w-3xl mt-12 sm:mt-16 rounded-2xl bg-muted/60 border border-border p-6 sm:p-8 text-center flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left space-y-1">
            <h3 className="font-default text-lg font-semibold text-foreground flex items-center gap-2">
              <MessageSquare className="size-5 text-primary" />
              <span>Have a specific question about your site?</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              You will speak directly with me, Yassine Essaadi.
            </p>
          </div>

          <Button asChild size="lg" className="w-full sm:w-auto shrink-0 font-medium">
            <Link to="/ContactPage">
              <span>Book a Free Consultation</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}